import { useState } from 'react';
import { Globe, Plus, Minus } from 'lucide-react';

interface CompetitorsTabProps {
  competitors: Array<{
    domain: string;
    url: string;
    headers?: {
      h1?: string[];
      h2?: string[];
      h3?: string[];
      h4?: string[];
    };
  }>;
}

export function CompetitorsTab({ competitors }: CompetitorsTabProps) {
  const [expandedCompetitors, setExpandedCompetitors] = useState<Set<string>>(new Set());

  const toggleCompetitor = (domain: string) => {
    setExpandedCompetitors(current => {
      const updated = new Set(current);
      if (updated.has(domain)) {
        updated.delete(domain);
      } else {
        updated.add(domain);
      }
      return updated;
    });
  };

  const getHeaderClassName = (level: string): string => {
    switch (level) {
      case 'h1':
        return 'text-xl font-bold text-gray-900';
      case 'h2':
        return 'text-lg font-semibold text-gray-800';
      case 'h3':
        return 'text-base font-medium text-gray-700';
      case 'h4':
        return 'text-sm font-medium text-gray-600';
      default:
        return 'text-sm text-gray-600';
    }
  };

  if (competitors.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No selected competitors available. Please select competitors in the Content tab.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {competitors.map((competitor, index) => (
        <div 
          key={competitor.domain} 
          className="border border-gray-200 rounded-md"
          data-competitor-section
        >
          <div 
            className="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer"
            onClick={() => toggleCompetitor(competitor.domain)}
          >
            <div className="flex items-center space-x-2">
              <span className="text-gray-500">#{index + 1}</span>
              <a 
                href={competitor.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800"
                onClick={(e) => e.stopPropagation()}
                data-competitor-domain
              >
                <div className="flex items-center">
                  <Globe className="h-4 w-4 mr-1" />
                  {competitor.domain}
                </div>
              </a>
            </div>
            <button className="text-gray-400 hover:text-gray-600">
              {expandedCompetitors.has(competitor.domain) ? (
                <Minus className="h-4 w-4" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
            </button>
          </div>
          {expandedCompetitors.has(competitor.domain) && competitor.headers && (
            <div 
              className="p-3 border-t border-gray-200 bg-gray-50"
              data-competitor-content
            >
              <div className="space-y-2">
                {Object.entries(competitor.headers).map(([level, texts]) => (
                  texts && texts.map((text, index) => (
                    <p 
                      key={`${level}-${index}`} 
                      className={getHeaderClassName(level)}
                      data-header-text
                      data-header-level={level}
                    >
                      {text}
                    </p>
                  ))
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}